<template>
  <div class="extension-column">
    <!-- 概算伝票の導線はカラムをスクロールしても見失わないよう見出しごと上端に固定する -->
    <div class="extension-column__header">
      <div class="overview-sec extension-column__header-title">
        次の延長
      </div>

      <div class="extension-column__header-actions">
        <Preview :slip-svg="slipSvg" />

        <print-btn
          subject="概算伝票"
          :print="printEstimatedSlip"
        />
      </div>
    </div>

    <!-- NOTE: 自動延長の発火窓では手動延長を止める（同じサイクルで二重に延長されるのを防ぐ） -->
    <p
      v-if="isAutoExtensionProcessing && (hasExtensionCandidateCourses || heldPlan)"
      class="extension-column__blocked ma-0 mb-2"
    >
      自動延長の処理中です。反映されるまで手動の延長はできません。
    </p>

    <!-- 設定中のプランは既存の延長候補を上書きする（候補一覧は出さず、自動延長もこの内容で発火する） -->
    <div
      v-if="heldPlan"
      class="held-plan-card"
    >
      <div class="held-plan-card__head">
        <div>
          <div class="held-plan-card__badge">
            設定中の延長
          </div>
          <div class="held-plan-card__name">
            {{ heldPlanCourseName }}
          </div>
          <div class="held-plan-card__note">
            会計まで有効（延長のたびにこの内容が使われます）
          </div>
        </div>

        <div class="held-plan-card__subtotal">
          <div class="held-plan-card__subtotal-amount">
            ¥{{ heldPlanSubtotal.toLocaleString() }}
          </div>
          <div class="held-plan-card__subtotal-label">
            小計（税サ前）
          </div>
        </div>
      </div>

      <div
        v-for="(heldPlanLine, heldPlanLineIdx) in heldPlanLines"
        :key="`heldPlanLines[${heldPlanLineIdx}]`"
        class="held-plan-card__line"
      >
        <span class="held-plan-card__line-name">{{ heldPlanLine.name }}</span>
        <span
          v-if="heldPlanLine.size > 1"
          class="held-plan-card__line-calc"
        >
          ¥{{ Number(heldPlanLine.sellingPrice).toLocaleString() }} × {{ heldPlanLine.size }}
        </span>
        <span class="held-plan-card__line-sub">
          ¥{{ (Number(heldPlanLine.sellingPrice) * heldPlanLine.size).toLocaleString() }}
        </span>
      </div>

      <div class="held-plan-card__actions">
        <v-btn
          outlined
          class="held-plan-card__edit-btn"
          :ripple="false"
          :disabled="isSubmittingHeldPlan"
          aria-label="設定中の延長を編集"
          @click="openHeldPlanEditor"
        >
          編集
        </v-btn>

        <v-btn
          color="primary"
          class="held-plan-card__submit-btn"
          :ripple="false"
          :loading="isSubmittingHeldPlan"
          :disabled="isSubmittingHeldPlan || isAutoExtensionProcessing"
          aria-label="設定中の延長で延長する"
          @click="submitHeldPlan"
        >
          延長する
        </v-btn>
      </div>

      <div class="held-plan-card__release">
        <v-btn
          text
          small
          :ripple="false"
          :loading="isReleasingHeldPlan"
          :disabled="isReleasingHeldPlan || isSubmittingHeldPlan"
          aria-label="設定を解除"
          @click="releaseHeldPlan"
        >
          設定を解除
        </v-btn>
      </div>
    </div>

    <!-- 設定中プランがある間は候補一覧を出さない（プランが候補を上書きする） -->
    <div v-if="!heldPlan && hasExtensionCandidateCourses">
      <extension-preset-card
        v-for="(extensionCandidate, extensionCandidateIdx) in extensionCandidates"
        :key="`extensionCandidates[${extensionCandidateIdx}]`"
        :candidate="extensionCandidate"
        :estimated-total="getTotal(extensionCandidateIdx)"
        :is-calculating="isCalculating"
        :recommended="extensionCandidateIdx === recommendedIndex"
        :submitting="submittingIdx === extensionCandidateIdx"
        :disabled="isAutoExtensionProcessing"
        @submit="submitExtension(extensionCandidateIdx)"
        @edit="openCandidateEditor(extensionCandidateIdx)"
      />
    </div>

    <div v-else-if="!heldPlan">
      <v-skeleton-loader
        v-if="isInitializing"
        height="160"
        type="card"
      />

      <p
        v-else
        class="ma-0 ml-2 pa-0 text-sm text--secondary"
      >
        延長候補はありません
      </p>
    </div>

    <extension-edit-dialog
      v-if="editingSeed"
      :seed="editingSeed"
      :table-id="tableId"
      :table-number="tableNumber"
      :table-prefix="tablePrefix"
      :table-suffix="tableSuffix"
      :custom-name="customName"
      :start-at="startAt"
      :expire-at="expireAt"
      :customer-count="customerCount"
      :total="total"
      :paid-in-advance-amount="paidInAdvanceAmount"
      :total-deposit-amount="totalDepositAmount"
      :disabled="isAutoExtensionProcessing"
      @held="handlePlanHeld($event)"
      @extended="applyExtensionResult($event)"
      @close="editingSeed = null"
    />
  </div>
</template>

<script>
import {
  ref,
  reactive,
  computed,
  inject,
  watch,
  onUnmounted,
  getCurrentInstance,
  onMounted,
} from '@vue/composition-api'
import {
  compact,
  find,
  map,
  sumBy,
} from 'lodash'
import VendingHistoryApi from '@/api/waiter/VendingHistory'
import ExtensionPlanApi from '@/api/waiter/ExtensionPlan'
import useExtensionCandidates, { pickRecommendedProposalIndex } from '@/views/composable/useExtensionCandidates'
import useExtensionEstimation from '@/views/composable/useExtensionEstimation'
import { useAutoExtensionStatus } from '@/views/composable/useAutoExtensionCountdown'
import useExtensionSlipPrint from '@/views/composable/useExtensionSlipPrint'
import useCurrentData from '@/views/composable/useCurrentData'
import useKitchenPrint from '@/views/composable/useKitchenPrint'
import useVendingHistory from '@/views/composable/useVendingHistory'
import {
  normalizeExtensionPlan,
  buildSeedFromCandidate,
  buildSeedFromExtensionPlan,
} from '@/views/composable/useExtensionPlan'
import ExtensionPresetCard from './ExtensionPresetCard.vue'
import ExtensionEditDialog from './ExtensionEditDialog.vue'
import PrintBtn from '@/views/components/util/PrintBtn.vue'
import Preview from '@/views/components/printer/Preview.vue'

export default {
  components: {
    ExtensionPresetCard,
    ExtensionEditDialog,
    PrintBtn,
    Preview,
  },
  props: {
    tableId: {
      type: [Number, String],
      validator: value => !Number.isNaN(Number(value)),
      required: true,
    },
    tableNumber: {
      type: [Number, String],
      validator: value => !Number.isNaN(Number(value)),
      required: true,
    },
    tablePrefix: {
      type: String,
      default: '',
    },
    tableSuffix: {
      type: String,
      default: '卓',
    },
    customName: {
      type: String,
      default: '',
    },
    startAt: {
      type: String,
      required: true,
    },
    expireAt: {
      type: String,
      required: false,
      default: null,
    },
    autoExtensionEnabled: {
      type: Boolean,
      default: false,
    },
    autoExtensionCourseName: {
      type: String,
      default: null,
    },
    customerCount: {
      type: [Number, String],
      validator: value => !Number.isNaN(Number(value)),
      required: true,
    },
    total: {
      type: [Number, String],
      required: true,
    },
    paidInAdvanceAmount: {
      type: [Number, String],
      required: false,
      default: 0,
    },
    totalDepositAmount: {
      type: [Number, String],
      default: 0,
    },
    hallName: {
      type: String,
      default: '',
    },
    // NOTE: 卓詳細(getTable)のattributes.extensionPlan。中身のキーだけスネークケースで届く
    extensionPlan: {
      type: Object,
      default: null,
    },
  },
  setup(props, { emit }) {
    const vm = getCurrentInstance().proxy

    const vendingHistories = inject('vendingHistories', ref([]))
    const courses = inject('courses', ref([]))
    const nominations = inject('nominations', ref([]))
    const items = inject('items', ref([]))

    const { currentUser, currentClub } = useCurrentData()
    const { printKitchenSlips } = useKitchenPrint()
    const { buildVendingHistoryTemp, buildVendingHistoriesToSubmit } = useVendingHistory()

    const submittingIdx = ref(null)
    const isSubmittingHeldPlan = ref(false)
    const isReleasingHeldPlan = ref(false)
    const editingSeed = ref(null)

    // NOTE: 保持/即延長の結果は即座に画面へ反映したいので、propsを初期値にローカルで持つ
    const heldPlan = ref(normalizeExtensionPlan(props.extensionPlan))
    watch(() => props.extensionPlan, newVal => {
      heldPlan.value = normalizeExtensionPlan(newVal)
    })

    const {
      extensionProposals,
      extensionCandidates,
      isInitializing,
      estimatedTotals,
      isCalculating,
      hasExtensionCandidateCourses,
      vendingHistoriesToSubmitOfArray,
      fetchExtensionProposals,
      getTotalTax,
      getTotal,
      resetCandidates,
    } = useExtensionCandidates(props)

    const recommendedIndex = computed(() => {
      return pickRecommendedProposalIndex(extensionProposals.value, vendingHistories.value)
    })

    // NOTE: 自動延長の発火窓（発火予定を過ぎて反映待ちの間）は手動延長を止める。
    // 判定は予告カウントダウンと同じ composable を共有し、条件を二重に持たない。
    const { isProcessing: isAutoExtensionProcessing } = useAutoExtensionStatus({
      enabled: () => props.autoExtensionEnabled,
      expireAt: () => props.expireAt,
      courseName: () => props.autoExtensionCourseName,
    })

    // NOTE: 延長作成が通ったあとの共通後処理。候補カード・保持プラン・編集モーダルの
    // どの経路から延長しても同じ反映（明細更新・キッチン印刷・候補の取り直し）になるようにまとめている
    const applyExtensionResult = async ({ vendingHistories: createdVendingHistories, table }) => {
      emit('updated:vending-histories', createdVendingHistories.data)
      // NOTE: updated:tableでprops.totalが変化しwatchが発火するが、
      // submittingIdxガードでblockされるので二重フェッチしない
      emit('updated:table', table.data)

      // キッチンプリンター印刷（既存の注文フローと同等）
      const tableCode = props.customName || `${props.tablePrefix}${props.tableNumber}${props.tableSuffix}`
      printKitchenSlips({
        vendingHistories: createdVendingHistories.data,
        staffName: currentUser.value?.name || '',
        hallName: props.hallName,
        tableCode,
        customerCount: props.customerCount,
      })

      vm.$toast.success('延長しました')

      // NOTE: プランは延長しても消費しない（卓が終わるまで有効）。
      // 卓の再取得(getTable)でpropsが更新されれば、そちらの内容に追従する

      // NOTE: 延長後は候補が変わるため取り直す
      resetCandidates()
      await fetchExtensionProposals()
    }

    // NOTE: 確認モーダルを挟まず即時にバックエンドへ延長データを作成する
    const submitExtension = async index => {
      if (submittingIdx.value !== null) return
      if (isAutoExtensionProcessing.value) return

      submittingIdx.value = index

      try {
        const res = await VendingHistoryApi.createVendingHistories({
          tableId: props.tableId,
          vendingHistories: vendingHistoriesToSubmitOfArray.value[index],
        })

        if (res?.status === 200) {
          await applyExtensionResult(res.data)
        } else {
          vm.$toast.error('延長に失敗しました')
        }
      } catch (_e) {
        vm.$toast.error('延長に失敗しました')
      } finally {
        submittingIdx.value = null
      }
    }

    const heldPlanSeed = () => buildSeedFromExtensionPlan(heldPlan.value, buildVendingHistoryTemp)

    const heldPlanLines = computed(() => {
      if (!heldPlan.value) return []

      const nameOf = (vendibles, vendibleId, fallback) => {
        return find(vendibles.value, vendible => Number(vendible.id) === Number(vendibleId))?.attributes?.name || fallback
      }

      return [
        {
          name: `${currentClub.value?.courseAlias || 'セット'}料金`,
          sellingPrice: heldPlan.value.course.sellingPrice,
          size: heldPlan.value.course.size || 1,
        },
        ...heldPlan.value.nominations.map(entry => ({
          name: nameOf(nominations, entry.vendibleId, currentClub.value?.nominationAlias || '指名'),
          sellingPrice: entry.sellingPrice,
          size: entry.size || 1,
        })),
        ...heldPlan.value.items.map(entry => ({
          name: nameOf(items, entry.vendibleId, 'アイテム'),
          sellingPrice: entry.sellingPrice,
          size: entry.size || 1,
        })),
      ]
    })

    const heldPlanCourseName = computed(() => {
      if (!heldPlan.value) return ''

      return find(courses.value, course => Number(course.id) === Number(heldPlan.value.course.vendibleId))
        ?.attributes?.name || '延長セット'
    })

    const heldPlanSubtotal = computed(() => {
      return sumBy(heldPlanLines.value, line => (Number(line.sellingPrice) || 0) * line.size)
    })

    const openCandidateEditor = index => {
      editingSeed.value = buildSeedFromCandidate(extensionCandidates.value[index])
    }

    const openHeldPlanEditor = () => {
      editingSeed.value = heldPlanSeed()
    }

    const handlePlanHeld = ({ extensionPlan }) => {
      heldPlan.value = extensionPlan
    }

    // NOTE: プランは卓が終わるまで残り自動延長にも効くので、明示的に取り消す導線が要る
    const releaseHeldPlan = async () => {
      if (isReleasingHeldPlan.value) return

      isReleasingHeldPlan.value = true

      try {
        const res = await ExtensionPlanApi.deleteExtensionPlan({ tableId: props.tableId })

        if (res?.status === 200) {
          heldPlan.value = null
          vm.$toast.success('延長の設定を解除しました')
        } else {
          vm.$toast.error('設定の解除に失敗しました')
        }
      } catch (_e) {
        vm.$toast.error('設定の解除に失敗しました')
      } finally {
        isReleasingHeldPlan.value = false
      }
    }

    // NOTE: 保持中プランをそのまま注文する。内容はモーダルと同じくプランから組み立てる
    const submitHeldPlan = async () => {
      if (isSubmittingHeldPlan.value) return
      if (isAutoExtensionProcessing.value) return

      isSubmittingHeldPlan.value = true

      try {
        const seed = heldPlanSeed()
        const res = await VendingHistoryApi.createVendingHistories({
          tableId: props.tableId,
          vendingHistories: buildVendingHistoriesToSubmit([seed.course, ...seed.nominations, ...seed.items]),
        })

        if (res?.status === 200) {
          // NOTE: プランは消費しない。同じ内容で何度でも延長できる
          await applyExtensionResult(res.data)
        } else {
          vm.$toast.error('延長に失敗しました')
        }
      } catch (_e) {
        vm.$toast.error('延長に失敗しました')
      } finally {
        isSubmittingHeldPlan.value = false
      }
    }

    // NOTE: 取り消し・注文・客数変更などで卓の合計が変わったら見積が古くなるため候補ごと取り直す
    // （延長実行中はsubmittingIdxガードでblockされ二重取得しない）
    watch(() => props.total, async () => {
      if (submittingIdx.value !== null) return

      resetCandidates()
      await fetchExtensionProposals()
    })

    // NOTE: 設定中プランは候補を上書きするので、概算伝票もプランの内容で出す。
    // 税サ計算が estimatedTaxPrice を書き戻すため、computedではなくプランの差し替え時だけ作り直す
    // （computedだと書き戻しで再評価が走り、税サが永久に確定しない）
    const heldPlanTemps = ref(null)

    watch(heldPlan, () => {
      if (!heldPlan.value) {
        heldPlanTemps.value = null

        return
      }

      const seed = heldPlanSeed()

      heldPlanTemps.value = {
        courseHistoryTemp: reactive(seed.course),
        nominationHistoriesTemp: map(seed.nominations, temp => reactive(temp)),
        itemHistoriesTemp: map(seed.items, temp => reactive(temp)),
        newHistoriesTemp: [],
      }
    }, { immediate: true })

    const heldPlanVendingHistoriesTemp = computed(() => {
      if (!heldPlanTemps.value) return []

      return compact([
        heldPlanTemps.value.courseHistoryTemp,
        ...heldPlanTemps.value.nominationHistoriesTemp,
        ...heldPlanTemps.value.itemHistoriesTemp,
      ])
    })

    const { estimatedTotal: heldPlanEstimatedTotal } = useExtensionEstimation({
      tableId: () => props.tableId,
      vendingHistoriesTemp: heldPlanVendingHistoriesTemp,
    })

    const slipCandidates = computed(() => {
      return heldPlanTemps.value ? [heldPlanTemps.value] : extensionCandidates.value
    })

    const slipEstimatedTotals = computed(() => {
      if (!heldPlanTemps.value) return estimatedTotals.value
      if (heldPlanEstimatedTotal.value === null) return []

      return [{ extensionCandidateIdx: 0, amount: heldPlanEstimatedTotal.value }]
    })

    // NOTE: YONAREZI 3 概算伝票
    const { slipSvg, printEstimatedSlip } = useExtensionSlipPrint({
      props,
      extensionCandidates: slipCandidates,
      estimatedTotals: slipEstimatedTotals,
      getTotalTax,
    })

    onUnmounted(() => resetCandidates())

    onMounted(async () => {
      await fetchExtensionProposals()
    })

    return {
      extensionCandidates,
      isInitializing,
      isCalculating,
      hasExtensionCandidateCourses,
      recommendedIndex,
      submittingIdx,
      isAutoExtensionProcessing,
      slipSvg,
      heldPlan,
      heldPlanLines,
      heldPlanCourseName,
      heldPlanSubtotal,
      isSubmittingHeldPlan,
      isReleasingHeldPlan,
      editingSeed,

      getTotal,
      submitExtension,
      submitHeldPlan,
      releaseHeldPlan,
      openCandidateEditor,
      openHeldPlanEditor,
      handlePlanHeld,
      applyExtensionResult,
      printEstimatedSlip,
    }
  },
}
</script>

<style lang="scss" scoped>
// スクロールコンテナは親の .overview-col（padding: 16px 16px 4px）。
// 負マージンでその余白ぶんを打ち消し、上端に貼り付いても下のカードが透けないよう地色を敷く
.extension-column__header {
  position: sticky;
  top: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin: -16px -16px 10px;
  padding: 14px 16px 8px;
  background: var(--overview-bg, #eef0f6);
}

.extension-column__header-title {
  margin-bottom: 0;
}

.extension-column__header-actions {
  display: flex;
  align-items: center;
  gap: 4px;

  // 見出し行に収めるため、既存の印刷・プレビューボタンを小さめに揃える
  ::v-deep .v-btn {
    height: 30px;
    min-width: 0;
    padding: 0 8px;
    font-size: 11px;
  }

  ::v-deep .v-btn .v-icon {
    font-size: 16px;
    margin-right: 2px;
  }
}

.extension-column__blocked {
  font-size: 13px;
  padding: 8px 10px;
  border-radius: 8px;
  background: var(--overview-surface-3, rgba(255, 255, 255, 0.06));
  color: var(--overview-text-1, inherit);
}

// 設定中プランは自動延長でも使われる「決まっている内容」なのでアクセント枠で表示する
.held-plan-card {
  background: var(--overview-surface-2, #18181f);
  border: 1px solid var(--overview-accent, #7c3aed);
  border-radius: 16px;
  padding: 14px;
  margin-bottom: 12px;
}

.held-plan-card__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 10px;
}

.held-plan-card__badge {
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--overview-accent-soft, rgba(124, 58, 237, 0.16));
  color: var(--overview-accent, #7c3aed);
}

.held-plan-card__name {
  font-size: 17px;
  font-weight: 700;
  margin-top: 4px;
}

.held-plan-card__note {
  font-size: 12px;
  opacity: 0.6;
  margin-top: 2px;
}

.held-plan-card__subtotal {
  text-align: right;
}

.held-plan-card__subtotal-amount {
  font-size: 19px;
  font-weight: 800;
}

.held-plan-card__subtotal-label {
  font-size: 11px;
  opacity: 0.55;
}

.held-plan-card__line {
  display: flex;
  align-items: center;
  font-size: 14px;
  padding: 8px 0;
  border-top: 1px solid var(--overview-border, rgba(255, 255, 255, 0.07));
}

.held-plan-card__line-name {
  flex: 1;
  min-width: 0;
  font-weight: 600;
}

.held-plan-card__line-calc {
  opacity: 0.6;
  margin-right: 10px;
  font-size: 13px;
}

.held-plan-card__line-sub {
  font-weight: 700;
}

.held-plan-card__actions {
  display: flex;
  gap: 8px;
  margin-top: 12px;
}

.held-plan-card__edit-btn {
  flex: 0 0 auto;
  min-width: 84px;
  height: 40px;
}

.held-plan-card__submit-btn {
  flex: 1;
  height: 40px;
}

// 解除は破棄操作なので主導線から離して控えめに置く
.held-plan-card__release {
  display: flex;
  justify-content: flex-end;
  margin-top: 4px;
}
</style>
